import { ReactNode } from 'react'
import { XClear } from '@/assets/icons'
import { getLineClampClass } from './getLineClampClass'

export type GlobalToastDarkPosition = 'bottom-right' | 'bottom-center'

const POSITION_CLASSES: Record<GlobalToastDarkPosition, string> = {
  'bottom-right': 'bottom-6 right-6',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
}

type GlobalToastDarkProps = {
  message: string
  icon: ReactNode
  onClose: () => void
  position: GlobalToastDarkPosition
  truncateAtLine?: number
}

/**
 * Dark variant of the global toast.
 * Presentational — unlike the slice-driven sibling toasts, the parent controls dismissal, icon, and position.
 */
export default function GlobalToastDark(props: GlobalToastDarkProps) {
  const { message, icon, onClose, position, truncateAtLine } = props

  return (
    <div
      data-ds-component="GlobalToastDark"
      data-ds-position={position}
      role="status"
      aria-live="polite"
      className={`fixed z-50 ${POSITION_CLASSES[position]} w-[320px] max-w-[calc(100vw-2rem)]`}
    >
      <div className="flex items-center gap-4 rounded-lg bg-true-gray-800 border border-true-gray-700 p-4">
        <div className="flex flex-1 items-center gap-3 min-w-0">
          <div className="flex items-center justify-center shrink-0 w-[33px] h-[33px] rounded-lg bg-true-gray-900 text-brand-neutral-50">{icon}</div>
          <div className={`flex-1 min-w-0 text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-200 ${getLineClampClass(truncateAtLine) ?? ''}`}>
            {message}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 inline-flex items-center justify-center p-1 rounded-lg text-brand-neutral-400 hover:bg-true-gray-700 hover:text-brand-neutral-200 focus:outline-none"
          aria-label="Close"
        >
          <XClear className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
